import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDown, ArrowUp, Columns3, Download, KeyRound, Mail, RefreshCw, RotateCcw, SlidersHorizontal, Sparkles, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { listAccounts, listBatches, runAccountTask } from "../../api/accounts";
import { getRunParams } from "../../api/settings";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { PaginationBar } from "../../components/pagination-bar";
import { SidePanel } from "../../components/side-panel";
import type { Account, RunParams } from "../../lib/types";
import { defaultRunParams } from "../../lib/run-params";
import {
  AccountDataGrid,
  accountGridColumnOptions,
  defaultAccountGridPreferences,
  normalizeAccountGridPreferences,
  readStoredGridPreferences,
  storeGridPreferences,
  type AccountGridPreferences,
  type ColumnKey
} from "./account-data-grid";
import { AccountDetailPanel } from "./account-detail-panel";
import { ImportAccountsDialog } from "./import-accounts-dialog";
import { RunParamsDialog } from "./run-params-dialog";

function moveColumn(order: ColumnKey[], key: ColumnKey, direction: -1 | 1) {
  const next = normalizeAccountGridPreferences({ order }).order;
  const index = next.indexOf(key);
  const target = index + direction;
  if (key === "actions" || index < 0 || target < 0 || target >= next.length - 1) return next;
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

function exportValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function safeExcelText(value: unknown): string {
  const text = exportValue(value);
  return /^[=+\-@\t\r]/.test(text) ? `'${text}` : text;
}

function taskCreationErrorMessage(reason: unknown): string {
  if (reason instanceof Error && reason.message.trim()) return reason.message.trim();
  return "未知错误，请稍后重试";
}

function summarizeTaskCreationFailures(failures: Array<{ accountId: number; message: string }>): string {
  const grouped = new Map<string, number[]>();
  for (const failure of failures) {
    const accountIds = grouped.get(failure.message) || [];
    accountIds.push(failure.accountId);
    grouped.set(failure.message, accountIds);
  }

  return Array.from(grouped.entries())
    .map(([message, accountIds]) => {
      const visibleIds = accountIds.slice(0, 5).join("、");
      const remaining = accountIds.length > 5 ? ` 等 ${accountIds.length} 个账号` : "";
      return `账号 ${visibleIds}${remaining}：${message}`;
    })
    .join("；");
}

function downloadSelectedAccountsExcel(accounts: Account[]) {
  const columns: Array<[string, (account: Account) => unknown]> = [
    ["ID", (account) => account.id],
    ["MJ ID", (account) => account.mj_id],
    ["当前邮箱", (account) => account.current_email],
    ["密码", (account) => account.password],
    ["当前邮箱密码", (account) => account.current_email_password],
    ["新邮箱", (account) => account.new_email],
    ["旧邮箱", (account) => account.old_email],
    ["旧邮箱密码", (account) => account.old_email_password],
    ["2FA Secret", (account) => account.mfa_secret],
    ["Token", (account) => account.token],
    ["Token 更新时间", (account) => account.token_updated_at],
    ["MJ Cookie", (account) => account.mj_cookie],
    ["Cookie 更新时间", (account) => account.cookie_updated_at],
    ["Cookie 信息", (account) => account.mj_cookie_info],
    ["当前 S5 代理", (account) => account.current_s5_proxy],
    ["代理获取时间", (account) => account.current_s5_proxy_fetched_at],
    ["批次", (account) => account.batch_no],
    ["备注", (account) => account.remark],
    ["警告启用", (account) => (account.warning_enabled ? "yes" : "no")],
    ["警告信息", (account) => account.warning_message],
    ["浏览器类型", (account) => account.browser_type],
    ["Browser Manager ID", (account) => account.browser_manager_id],
    ["Browser Profile ID", (account) => account.browser_profile_id],
    ["删除时间", (account) => account.deleted_at],
    ["最近任务类型", (account) => account.latest_task?.task_type],
    ["最近任务状态", (account) => account.latest_task?.status],
    ["最近任务消息", (account) => account.latest_task?.error_message || account.latest_task?.execute_message],
    ["创建时间", (account) => account.created_at],
    ["更新时间", (account) => account.updated_at]
  ];
  const header = columns.map(([label]) => `<th>${escapeHtml(label)}</th>`).join("");
  const rows = accounts
    .map((account) => (
      `<tr>${columns
        .map(([, getter]) => `<td style="mso-number-format:'\\@';">${escapeHtml(safeExcelText(getter(account)))}</td>`)
        .join("")}</tr>`
    ))
    .join("");
  const html = `<!doctype html><html><head><meta charset="utf-8"></head><body><table border="1"><thead><tr>${header}</tr></thead><tbody>${rows}</tbody></table></body></html>`;
  const blob = new Blob([html], { type: "application/vnd.ms-excel;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
  link.href = url;
  link.download = `discord-accounts-selected-${timestamp}.xls`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function ColumnSettingsDialog({
  preferences,
  onChange,
  onClose
}: {
  preferences: AccountGridPreferences;
  onChange: (preferences: AccountGridPreferences) => void;
  onClose: () => void;
}) {
  const normalized = normalizeAccountGridPreferences(preferences);
  const hidden = new Set(normalized.hidden);
  const visibleCount = accountGridColumnOptions.filter((column) => !hidden.has(column.key)).length;

  function commit(next: AccountGridPreferences) {
    const normalizedNext = normalizeAccountGridPreferences(next);
    onChange(normalizedNext);
    storeGridPreferences(normalizedNext);
  }

  function toggleColumn(key: ColumnKey, checked: boolean) {
    const nextHidden = new Set(normalized.hidden);
    if (checked) nextHidden.delete(key);
    else nextHidden.add(key);
    commit({ ...normalized, hidden: Array.from(nextHidden) });
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-slate-950/30 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
        <div className="flex h-12 items-center justify-between border-b px-4">
          <h2 className="text-sm font-semibold">账号表列设置</h2>
          <Button onClick={onClose}>
            <X size={14} />
          </Button>
        </div>
        <div className="max-h-[70vh] space-y-2 overflow-auto p-4">
          {accountGridColumnOptions.map((column) => {
            const checked = !hidden.has(column.key);
            const orderIndex = normalized.order.indexOf(column.key);
            return (
              <div key={column.key} className="grid grid-cols-[1fr_auto] items-center gap-3 rounded-md border px-3 py-2 text-xs">
                <label className="flex min-w-0 items-center gap-2">
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={checked && visibleCount <= 1}
                    onChange={(event) => toggleColumn(column.key, event.target.checked)}
                  />
                  <span className="truncate font-medium">{column.title}</span>
                </label>
                <div className="flex items-center gap-1">
                  <Button
                    disabled={orderIndex <= 0}
                    onClick={() => commit({ ...normalized, order: moveColumn(normalized.order, column.key, -1) })}
                  >
                    <ArrowUp size={13} />
                  </Button>
                  <Button
                    disabled={orderIndex < 0 || orderIndex >= normalized.order.length - 2}
                    onClick={() => commit({ ...normalized, order: moveColumn(normalized.order, column.key, 1) })}
                  >
                    <ArrowDown size={13} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="flex justify-between gap-2 border-t p-3">
          <p className="text-xs leading-8 text-slate-500">偏好保存到当前浏览器，用来降低横向滚动。</p>
          <Button onClick={() => commit(defaultAccountGridPreferences)}>
            <RotateCcw size={14} />
            恢复默认
          </Button>
        </div>
      </div>
    </div>
  );
}

export function AccountsPage() {
  const queryClient = useQueryClient();
  const [keyword, setKeyword] = useState("");
  const [taskStatus, setTaskStatus] = useState("");
  const [batchNo, setBatchNo] = useState("");
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [selected, setSelected] = useState<Account | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [paramsOpen, setParamsOpen] = useState(false);
  const [runParamsOverride, setRunParamsOverride] = useState<RunParams | null>(null);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [gridPreferences, setGridPreferences] = useState<AccountGridPreferences>(() => readStoredGridPreferences());

  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [batchNo, includeDeleted, keyword, perPage, taskStatus]);

  const filters = useMemo(
    () => ({ page, per_page: perPage, keyword, task_status: taskStatus, batch_no: batchNo, include_deleted: includeDeleted }),
    [batchNo, includeDeleted, keyword, page, perPage, taskStatus]
  );
  const accountsQuery = useQuery({ queryKey: ["accounts", filters], queryFn: () => listAccounts(filters) });
  const batchesQuery = useQuery({ queryKey: ["batches"], queryFn: () => listBatches({ limit: 200 }) });
  const runParamsQuery = useQuery({ queryKey: ["run-params"], queryFn: getRunParams });
  const defaultParams = runParamsQuery.data || defaultRunParams;
  const runParams = runParamsOverride || defaultParams;
  const hasActiveAccounts = Boolean(
    accountsQuery.data?.accounts.some(
      (account) => account.active_task || account.latest_task?.status === "queued" || account.latest_task?.status === "leased" || account.latest_task?.status === "running"
    )
  );
  const selectedAccounts = useMemo(() => (accountsQuery.data?.accounts || []).filter((account) => selectedIds.has(account.id)), [accountsQuery.data?.accounts, selectedIds]);
  const runnableSelectedAccounts = useMemo(() => selectedAccounts.filter((account) => !account.deleted_at), [selectedAccounts]);
  const batchRunMutation = useMutation({
    mutationFn: async (taskType: string) => {
      const results = await Promise.allSettled(runnableSelectedAccounts.map((account) => runAccountTask(account.id, taskType, runParams)));
      const succeeded = results.filter((result) => result.status === "fulfilled").length;
      const failures = results.flatMap((result, index) =>
        result.status === "rejected"
          ? [{ accountId: runnableSelectedAccounts[index].id, message: taskCreationErrorMessage(result.reason) }]
          : []
      );
      return { succeeded, failures };
    },
    onSuccess: ({ succeeded, failures }) => {
      if (succeeded) toast.success(`已创建 ${succeeded} 个任务`);
      if (failures.length) {
        toast.error(`${failures.length} 个任务创建失败`, {
          description: summarizeTaskCreationFailures(failures),
          duration: 10000
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["accounts"] });
      void queryClient.invalidateQueries({ queryKey: ["tasks"] });
    },
    onError: (error) => toast.error(error instanceof Error ? error.message : "批量任务创建失败")
  });

  useEffect(() => {
    if (!hasActiveAccounts) return undefined;
    const timer = window.setInterval(() => {
      void accountsQuery.refetch();
    }, 3000);
    return () => window.clearInterval(timer);
  }, [accountsQuery, hasActiveAccounts]);

  return (
    <div className="flex h-full flex-col gap-3 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Input className="w-64" placeholder="搜索 ID、邮箱、备注、批次；可批量粘贴" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
          <select className="h-8 max-w-56 rounded-md border border-slate-300 bg-white px-2 text-xs" value={batchNo} onChange={(event) => setBatchNo(event.target.value)}>
            <option value="">全部批次</option>
            {(batchesQuery.data?.batches || []).map((batch) => (
              <option key={batch.batch_no} value={batch.batch_no}>
                {batch.batch_no} ({batch.account_count})
              </option>
            ))}
          </select>
          <select className="h-8 rounded-md border border-slate-300 bg-white px-2 text-xs" value={taskStatus} onChange={(event) => setTaskStatus(event.target.value)}>
            <option value="">全部任务状态</option>
            <option value="active">queued/running</option>
            <option value="queued">queued</option>
            <option value="running">running</option>
            <option value="success">success</option>
            <option value="failed">failed</option>
          </select>
          <label className="flex h-8 items-center gap-1 rounded-md border bg-white px-2 text-xs">
            <input type="checkbox" checked={includeDeleted} onChange={(event) => setIncludeDeleted(event.target.checked)} />
            已删除
          </label>
        </div>
        <div className="flex items-center gap-2">
          {selectedAccounts.length ? (
            <>
              <span className="text-xs text-slate-500">已选 {selectedAccounts.length}</span>
              <Button disabled={!runnableSelectedAccounts.length || batchRunMutation.isPending} onClick={() => batchRunMutation.mutate("discord_login")}>
                <KeyRound size={14} />
                批量登录
              </Button>
              <Button disabled={!runnableSelectedAccounts.length || batchRunMutation.isPending} onClick={() => batchRunMutation.mutate("discord_protocol_login")}>
                <KeyRound size={14} />
                批量协议登录
              </Button>
              <Button disabled={!runnableSelectedAccounts.length || batchRunMutation.isPending} onClick={() => batchRunMutation.mutate("discord_email")}>
                <Mail size={14} />
                批量换邮
              </Button>
              <Button disabled={!runnableSelectedAccounts.length || batchRunMutation.isPending} onClick={() => batchRunMutation.mutate("mj_login")}>
                <Sparkles size={14} />
                批量 MJ 协议
              </Button>
              <Button
                onClick={() => {
                  downloadSelectedAccountsExcel(selectedAccounts);
                  toast.success(`已导出 ${selectedAccounts.length} 个账号`);
                }}
              >
                <Download size={14} />
                导出Excel
              </Button>
              <Button onClick={() => setSelectedIds(new Set())}>
                <X size={14} />
                清除选择
              </Button>
            </>
          ) : null}
          <Button onClick={() => setParamsOpen(true)}>
            <SlidersHorizontal size={14} />
            执行参数
          </Button>
          {runParamsOverride ? <span className="rounded bg-amber-50 px-2 py-1 text-xs font-medium text-amber-700">本次覆盖已启用</span> : null}
          <Button onClick={() => setColumnsOpen(true)}>
            <Columns3 size={14} />
            列设置
          </Button>
          <Button onClick={() => setImportOpen(true)}>
            <Upload size={14} />
            导入账号
          </Button>
          <Button onClick={() => void accountsQuery.refetch()}>
            <RefreshCw size={14} />
            刷新
          </Button>
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2 text-xs">
        <div className="rounded-md border bg-white p-2">总数 <b>{accountsQuery.data?.total ?? 0}</b></div>
        <div className="rounded-md border bg-white p-2">本页排队/运行 <b>{accountsQuery.data?.accounts.filter((a) => a.active_task).length ?? 0}</b></div>
        <div className="rounded-md border bg-white p-2">本页失败 <b>{accountsQuery.data?.accounts.filter((a) => a.latest_task?.status === "failed").length ?? 0}</b></div>
        <div className="rounded-md border bg-white p-2">本页已删除 <b>{accountsQuery.data?.accounts.filter((a) => a.deleted_at).length ?? 0}</b></div>
      </div>
      <div className="min-h-0 flex-1">
        <AccountDataGrid
          accounts={accountsQuery.data?.accounts || []}
          onSelect={setSelected}
          selectedIds={selectedIds}
          onSelectedIdsChange={setSelectedIds}
          preferences={gridPreferences}
        />
      </div>
      <PaginationBar
        total={accountsQuery.data?.total ?? 0}
        page={page}
        perPage={perPage}
        onPageChange={(next) => {
          setSelectedIds(new Set());
          setPage(next);
        }}
        onPerPageChange={setPerPage}
      />
      <SidePanel title="账号详情" open={Boolean(selected)} onClose={() => setSelected(null)}>
        {selected ? <AccountDetailPanel account={selected} runParams={runParams} onAccountChanged={setSelected} /> : null}
      </SidePanel>
      <ImportAccountsDialog open={importOpen} onClose={() => setImportOpen(false)} />
      {columnsOpen ? (
        <ColumnSettingsDialog preferences={gridPreferences} onChange={setGridPreferences} onClose={() => setColumnsOpen(false)} />
      ) : null}
      <RunParamsDialog
        open={paramsOpen}
        onClose={() => setParamsOpen(false)}
        onSaved={(next) => {
          queryClient.setQueryData<RunParams>(["run-params"], next);
          void queryClient.invalidateQueries({ queryKey: ["config"] });
          void queryClient.invalidateQueries({ queryKey: ["runtime"] });
          toast.success("后续行内任务将使用新的默认参数");
        }}
        initialParams={defaultParams}
        overrideParams={runParamsOverride}
        onApplyOverride={(next) => {
          setRunParamsOverride(next);
          toast.success("本次覆盖已启用，后续行内/批量任务会使用这份参数");
        }}
        onClearOverride={() => {
          setRunParamsOverride(null);
          toast.info("已清除本次覆盖，恢复使用默认参数");
        }}
      />
    </div>
  );
}
